
import React from 'react';
import { Building, Factory, ShoppingCart, BarChart2, Globe, Stethoscope } from 'lucide-react';
import Heading from '@/components/ui/heading';

const UseCases = () => {
  const industries = [
    {
      id: "finance",
      title: "Financial Services",
      description: "Access in-depth analysis of market trends, competitive landscape, and investment opportunities in the financial sector.",
      icon: <BarChart2 className="h-8 w-8 text-blue-400" />
    },
    {
      id: "retail",
      title: "Retail & E-commerce",
      description: "Obtain consumer behavior insights, market share analysis, and growth opportunities for retail businesses.",
      icon: <ShoppingCart className="h-8 w-8 text-blue-400" />
    },
    {
      id: "manufacturing",
      title: "Manufacturing",
      description: "Explore supply chain analysis, production optimization insights, and competitive positioning for manufacturers.",
      icon: <Factory className="h-8 w-8 text-blue-400" />
    },
    {
      id: "healthcare",
      title: "Healthcare",
      description: "Understand healthcare market dynamics, regulatory analysis, and competitive intelligence for healthcare organizations.",
      icon: <Stethoscope className="h-8 w-8 text-blue-400" />
    },
    {
      id: "corporate",
      title: "Corporate Strategy",
      description: "Leverage strategic planning insights, merger & acquisition analysis, and business expansion opportunities.",
      icon: <Building className="h-8 w-8 text-blue-400" />
    },
    {
      id: "global",
      title: "Global Markets",
      description: "Gain international market entry analysis, geopolitical risk assessment, and global expansion strategies.",
      icon: <Globe className="h-8 w-8 text-blue-400" />
    }
  ];

  return (
    <section className="py-24 bg-gray-800 relative overflow-hidden">
      {/* Floating background elements */}
      <div className="absolute inset-0 bg-gradient-to-br from-gray-800 to-gray-900"></div>
      <div className="absolute opacity-10 -top-24 -left-24 w-96 h-96 bg-blue-500 rounded-full blur-3xl"></div>
      <div className="absolute opacity-10 -bottom-24 -right-24 w-96 h-96 bg-indigo-500 rounded-full blur-3xl"></div>
      
      <div className="container mx-auto px-4 relative z-10">
        <div className="text-center mb-16">
          <Heading level={2} className="mb-4">
            Industry Coverage
          </Heading>
          <p className="text-lg text-gray-300 max-w-3xl mx-auto">
            AnalystX provides market intelligence on any industry, delivering targeted insights for your specific sector.
          </p>
        </div>

        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-8 max-w-6xl mx-auto">
          {industries.map((industry) => (
            <div 
              key={industry.id} 
              className="bg-gradient-to-br from-gray-800/80 to-gray-900/80 border border-gray-700/30 rounded-xl p-6 
                        transition-all duration-300 hover:border-blue-500/30 hover:shadow-lg hover:shadow-blue-900/20 
                        group relative overflow-hidden"
            >
              {/* Floating icon animation */}
              <div className="absolute -right-4 -top-4 opacity-5 group-hover:opacity-10 transition-opacity duration-500">
                <div className="text-blue-400 transform scale-[4] group-hover:scale-[4.5] transition-transform duration-500">
                  {industry.icon}
                </div>
              </div>
              
              <div className="flex flex-col h-full z-10 relative">
                <div className="flex items-center mb-4">
                  <div className="p-2 bg-blue-900/20 rounded-lg mr-3">
                    {industry.icon}
                  </div>
                  <h3 className="text-xl font-semibold text-white">
                    {industry.title}
                  </h3>
                </div>
                
                <p className="text-gray-300 mt-2">
                  {industry.description}
                </p>
              </div>
            </div>
          ))}
        </div>
      </div>
    </section>
  );
};

export default UseCases;
